import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, AlertTriangle, Ban, Flag, Loader2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACTIONS = {
  warn: { title: "Profil verwarnen", desc: "Der Nutzer sieht die Verwarnung in seinem Profil und bekommt eine Push-Nachricht.", url: (id) => `/mod/users/${id}/warn`, ok: "Verwarnung gesendet" },
  request: { title: "Sperrung beantragen", desc: "Der Antrag geht an das itzPlay-Team, das über die Sperrung entscheidet.", url: (id) => `/mod/users/${id}/ban-request`, ok: "Antrag an die Admins gesendet" },
  ban: { title: "Profil sperren", desc: "Der Nutzer wird sofort abgemeldet und kann sich nicht mehr anmelden.", url: (id) => `/admin/users/${id}/ban`, ok: "Profil gesperrt" },
};

export function ReasonDialog({ action, userId, userName, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const cfg = ACTIONS[action];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(cfg.url(userId), { reason }); toast.success(cfg.ok); onDone(); onClose(); } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={`mod-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{cfg.title}</DialogTitle>
          <DialogDescription>{userName}: {cfg.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Textarea data-testid="mod-reason-input" required minLength={3} maxLength={500} rows={4} className="rounded-xl" placeholder="Grund (wird dem Nutzer angezeigt)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="submit" disabled={busy || reason.trim().length < 3} data-testid="mod-reason-submit" className="w-full h-12 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {cfg.title}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const WarningsList = ({ items }) => (
  <ul className="space-y-2" data-testid="mod-warnings-list">
    {items.length === 0 && <li className="text-xs text-slate-400">Keine Verwarnungen.</li>}
    {items.map((w) => (
      <li key={w.id} className="text-sm p-3 rounded-xl bg-amber-50 border border-amber-100">
        <p className="text-slate-700">{w.reason}</p>
        <p className="text-[11px] text-slate-400 mt-1">{fmtDate(w.created_at)} · von {w.by_name}</p>
      </li>
    ))}
  </ul>
);

export function ModerationPanel({ profile, onChanged }) {
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const isAdmin = user?.role === "admin";
  const load = useCallback(() => api.get(`/mod/users/${profile.user_id}/warnings`).then(({ data }) => setWarnings(data)), [profile.user_id]);
  useEffect(() => { load(); }, [load]);
  const canModerate = !profile.is_me && !profile.is_creator && (isAdmin || !profile.is_moderator);
  if (!canModerate) return null;

  const toggleRole = async () => {
    const role = profile.is_moderator ? "user" : "moderator";
    try { await api.put(`/admin/users/${profile.user_id}/role`, { role }); toast.success(role === "moderator" ? `${profile.name} ist jetzt Moderator` : "Moderator-Rechte entfernt"); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  const unban = async () => {
    try { await api.post(`/admin/users/${profile.user_id}/unban`); toast.success("Sperre aufgehoben"); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  const done = () => { load(); onChanged(); };

  return (
    <div className="bg-white rounded-3xl p-5 border border-violet-200 space-y-4" data-testid="moderation-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-violet-600" /> Moderation</h3>
        {profile.banned && <span className="text-xs font-bold px-3 py-1 rounded-full bg-red-100 text-red-700" data-testid="mod-banned-badge">Gesperrt</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setDialog("warn")} data-testid="mod-warn-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-amber-100 text-amber-800 text-sm font-bold"><AlertTriangle className="w-4 h-4" /> Verwarnen</button>
        {!isAdmin && <button onClick={() => setDialog("request")} data-testid="mod-ban-request-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-red-50 text-red-700 text-sm font-bold"><Flag className="w-4 h-4" /> Sperrung beantragen</button>}
        {isAdmin && !profile.banned && <button onClick={() => setDialog("ban")} data-testid="mod-ban-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-red-600 text-white text-sm font-bold"><Ban className="w-4 h-4" /> Sperren</button>}
        {isAdmin && <button onClick={toggleRole} data-testid="mod-role-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-violet-100 text-violet-700 text-sm font-bold"><ShieldCheck className="w-4 h-4" /> {profile.is_moderator ? "Moderator entfernen" : "Zum Moderator ernennen"}</button>}
        {isAdmin && profile.banned && <button onClick={unban} data-testid="mod-unban-btn" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-emerald-600 text-white text-sm font-bold"><Unlock className="w-4 h-4" /> Sperre aufheben</button>}
      </div>
      <div><p className="text-xs font-semibold text-slate-500 mb-2">Verwarnungen ({warnings.length})</p><WarningsList items={warnings} /></div>
      {dialog && <ReasonDialog action={dialog} userId={profile.user_id} userName={profile.name} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}
